import {api} from '../../api.js';
import type {AuthSnapshot} from '../../auth.js';
import {apiUrl} from '../../config.js';
import {escapeHtml} from '../../layout.js';
import {validateLitigationWorkbookCreation, type LitigationCreationSpace} from '../../../common/litigationWorkbookCreation.js';

/** Shared by the workbook list and the active ledger; never creates a case row. */
export async function openLitigationWorkbookCreation(root: HTMLElement, auth: AuthSnapshot, canStart: () => boolean = () => true): Promise<void> {
  if (!canStart()) return;
  const {spaces} = await api.get<{spaces: LitigationCreationSpace[]}>(apiUrl('/api/legal-litigation/workbook-creation-options'));
  if (!canStart()) return;
  const host = root.querySelector<HTMLElement>('#llDialogHost')!;
  host.innerHTML = `<dialog class="la-dialog ll-create-dialog" aria-label="新建台账"><form>
    <header><h2>新建台账</h2><button type="button" class="la-dialog-close" data-close aria-label="关闭"><img src="/assets/icons/archive/x.svg" alt=""></button></header><div class="la-dialog-body">
    <label class="ll-create-field"><span>台账名称 <span class="ll-required">*</span></span><input class="form-input" name="name" required maxlength="80" autocomplete="off" placeholder="请输入台账名称"></label>
    <fieldset class="ll-create-scope"><legend>台账范围 <span class="ll-required">*</span></legend><div class="ll-create-scope-options">
      <label class="ll-create-scope-card"><input type="radio" name="scope" value="personal" required><span><strong>个人工作台账</strong><small>仅自己可见</small></span></label>
      <label class="ll-create-scope-card"><input type="radio" name="scope" value="team" required><span><strong>团队工作台账</strong><small>团队成员可见</small></span></label>
    </div></fieldset>
    <label class="ll-create-field"><span>保存空间 <span class="ll-required">*</span></span><select class="form-input" name="organizationId" required disabled><option value="">请先选择台账范围</option></select></label>
    <div class="ll-create-location" hidden><span class="ll-create-folder-icon" aria-hidden="true"></span><span data-location></span></div><p data-error role="alert"></p>
    </div><footer><button type="button" class="la-secondary" data-cancel>取消</button><button class="la-primary" type="submit">创建台账</button></footer>
    </form></dialog>`;
  const dialog = host.querySelector<HTMLDialogElement>('dialog')!;
  const form = dialog.querySelector('form')!;
  const scope = form.elements.namedItem('scope') as RadioNodeList;
  const organization = form.elements.namedItem('organizationId') as HTMLSelectElement;
  const error = dialog.querySelector<HTMLElement>('[data-error]')!;
  const submit = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
  let busy = false;
  let created: {id: string; organizationId: string} | null = null;
  const switchSpace = async (id: string) => {
    if (id !== auth.organization.id) await api.post(apiUrl(`/api/organizations/enterprise-spaces/${encodeURIComponent(id)}/select`));
    try { localStorage.setItem('tax-assistant-space-scope', spaces.find((space) => space.id === id)?.scope === 'team' ? 'enterprise' : 'personal'); } catch { /* Saving a UI preference must not block opening the created ledger. */ }
  };
  /**
   * 按已选范围显示保存位置。方案由服务端绑定，这里不提供选择。
   */
  const paintSpace = () => {
    const space = spaces.find((item) => item.id === organization.value && item.scope === scope.value);
    dialog.querySelector<HTMLElement>('.ll-create-location')!.hidden = !space;
    dialog.querySelector('[data-location]')!.textContent = space ? `${space.scope === 'team' ? '团队空间' : '个人空间'} / 诉讼台账` : '';
    submit.disabled = !space;
  };
  dialog.querySelector('.ll-create-scope')!.addEventListener('change', () => {
    error.textContent = '';
    const available = spaces.filter((item) => item.scope === scope.value);
    organization.disabled = !available.length;
    organization.innerHTML = `<option value="">${available.length ? '请选择保存空间' : '暂无可用空间'}</option>${available.map((space) => `<option value="${escapeHtml(space.id)}">${escapeHtml(space.name)}</option>`).join('')}`;
    if (available.length === 1) organization.value = available[0].id;
    if (scope.value === 'team' && !available.length) error.textContent = '请先创建或加入团队空间';
    paintSpace();
  });
  organization.addEventListener('change', paintSpace);
  dialog.querySelector('[data-cancel]')!.addEventListener('click', () => { if (!busy) dialog.close(); });
  dialog.querySelector('[data-close]')!.addEventListener('click', () => { if (!busy) dialog.close(); });
  dialog.addEventListener('cancel', (event) => { if (busy) event.preventDefault(); });
  dialog.addEventListener('click', (event) => event.stopPropagation());
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (busy || !canStart()) return;
    void (async () => {
      try {
        const input = validateLitigationWorkbookCreation(spaces, {name: (form.elements.namedItem('name') as HTMLInputElement).value, scope: scope.value, organizationId: organization.value});
        busy = true;
        submit.disabled = true;
        if (!created) {
          const result = await api.post<{workbook: {id: string; organizationId: string}}>(apiUrl('/api/legal-litigation/workbooks'), input);
          created = result.workbook;
        }
        await switchSpace(created.organizationId);
        window.location.assign(`/tax-assistant/legal-litigation/${encodeURIComponent(created.id)}`);
      } catch (failure) {
        error.textContent = `${created ? '台账已创建，点击按钮重试打开。' : ''}${failure instanceof Error ? failure.message : '创建失败'}`;
        if (created) {
          form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select').forEach((input) => input.disabled = true);
          submit.textContent = '打开已创建台账';
        }
        busy = false;
        submit.disabled = false;
      }
    })();
  });
  submit.disabled = true;
  dialog.showModal();
}
